/* eslint-disable @typescript-eslint/no-empty-function */
import React from 'react';
import {
  AddonProduct,
  useAddons,
  useAddonsSelector,
  UseAddonsSelectorResponse,
} from './useAddons';

interface AddonsContext extends UseAddonsSelectorResponse {
  addonProducts: AddonProduct[];
  addonsDiscounts?: AddonsDiscounts;
  subscription: Subscription;
  sellingPlans: SellingPlan[];
  shopConfiguration: ShopConfiguration;
  isLoading: boolean;
}

const AddonsContext = React.createContext({
  isLoading: false,
  handleSelect: (_: { id: string; quantity: number }) => {},
  handleConfirm: (_: unknown) => {},
  onAddBundle: (..._: unknown[]) => {},
  handleSaveBundle: (..._params: any) => {},
  selectAddon: (_: number, _isBundle: boolean) => {},
  addonProducts: [] as AddonProduct[],
  isPanelOpen: false,
  selectedBundle: null,
  closePanel: () => {},
  subscription: null,
  sellingPlans: [],
  shopConfiguration: null,
});

AddonsContext.displayName = 'AddonsContext';

const useAddonsContext = (): AddonsContext => {
  const context = React.useContext(AddonsContext);
  if (context === undefined) {
    throw new Error('useAddonsContext must be used within a AddonsProvider');
  }
  return context;
};

interface AddonsProviderProps {
  children: React.ReactNode;
  addonsDiscounts?: AddonsDiscounts;
  onAddAddons: (
    addons: (GlobalVariant & { quantity: number; productName: string } & {
      line: { id: string; oneTimeAddon?: boolean };
    })[],
  ) => void;
  onAddBundle: (
    bundleProps,
    _lineId: string,
    selectedItems: any,
    sellingPlan?: string | number,
    discountCodes?: string[],
    oneTimeAddon?: boolean,
    addonGroupId?: string,
  ) => void;
  subscription: Subscription;
  sellingPlans: SellingPlan[];
  shopConfiguration: ShopConfiguration;
}

export const AddonsProvider: React.FC<AddonsProviderProps> = ({
  children,
  onAddAddons,
  onAddBundle,
  addonsDiscounts,
  subscription,
  sellingPlans,
  shopConfiguration,
}): React.ReactElement => {
  const { isLoading, addons, addonProducts, saveAddonsAdded, saveBundleAdded } =
    useAddons(onAddAddons, onAddBundle, addonsDiscounts);
  const context = {
    ...useAddonsSelector(
      addons,
      addonProducts,
      saveAddonsAdded,
      saveBundleAdded,
    ),
    onAddBundle,
    addonProducts,
    addonsDiscounts,
    isLoading,
    subscription,
    sellingPlans,
    shopConfiguration,
  };

  return (
    <AddonsContext.Provider value={context}>{children}</AddonsContext.Provider>
  );
};

export default useAddonsContext;
